import React from "react";
import { fireEvent, render, screen, waitFor } from "@testing-library/react";
import "@testing-library/jest-dom";
import GroupMembers from "./GroupMembers";

const mockAuthFetch = jest.fn();
const mockListUsers = jest.fn();
let mockGroups = [
  { id: "group-1", name: "reviewers", display_name: "Reviewers" },
];
let memberIds: string[];
const mockRefresh = jest.fn();

jest.mock("../../../providers/WorkspaceProvider", () => ({
  useWorkspaceContext: () => ({ workspace: { id: "workspace-1" } }),
}));
jest.mock("../../../services/api", () => ({
  useWorkspaceUserList: () => mockListUsers,
}));
jest.mock("../../../utils/useAuthFetch", () => ({
  useAuthFetch: () => mockAuthFetch,
}));
jest.mock("../hooks/useGroups", () => ({
  useGroups: () => ({
    groups: mockGroups,
    loading: false,
    error: null,
    refresh: mockRefresh,
  }),
}));

describe("GroupMembers", () => {
  beforeEach(() => {
    mockGroups = [
      { id: "group-1", name: "reviewers", display_name: "Reviewers" },
    ];
    mockRefresh.mockReset().mockResolvedValue(undefined);
    memberIds = ["user-1"];
    mockAuthFetch.mockReset().mockImplementation(async (url, tag, options) => {
      if (tag === "list group members")
        return memberIds.map((user_id) => ({ user_id }));
      if (tag === "load group application data") return { allowed: false };
      if (tag === "update group application data")
        return JSON.parse(options.body);
      if (tag === "add group member") {
        memberIds.push(JSON.parse(options.body).user_id);
        return { user_id: memberIds[memberIds.length - 1] };
      }
      if (tag === "remove group member") {
        memberIds = memberIds.filter((id) => !url.endsWith(id));
        return null;
      }
      throw new Error(`Unexpected request: ${tag}`);
    });
    mockListUsers.mockReset().mockResolvedValue([
      { user_id: "user-1", email: "ada@example.com", status: "accepted" },
      { user_id: "user-2", email: "ben@example.com", status: "accepted" },
      { user_id: "user-3", email: "cal@example.com", status: "invited" },
    ]);
  });

  it("adds an accepted workspace user and removes a group member", async () => {
    render(<GroupMembers />);
    expect(await screen.findByText("ada@example.com")).toBeInTheDocument();

    fireEvent.click(screen.getByRole("combobox", { name: "Workspace user" }));
    expect(
      screen.getByRole("option", { name: "ben@example.com" }),
    ).toBeInTheDocument();
    expect(
      screen.queryByRole("option", { name: "cal@example.com" }),
    ).toBeNull();
    fireEvent.click(screen.getByRole("option", { name: "ben@example.com" }));
    fireEvent.click(screen.getByRole("button", { name: "Add member" }));
    await waitFor(() =>
      expect(mockAuthFetch).toHaveBeenCalledWith(
        "/api/workspace/myvault/groups/group-1/members",
        "add group member",
        expect.objectContaining({
          method: "POST",
          body: JSON.stringify({ user_id: "user-2" }),
        }),
      ),
    );
    expect(await screen.findByText("ben@example.com")).toBeInTheDocument();

    fireEvent.click(
      screen.getByRole("button", { name: "Remove ada@example.com" }),
    );
    await waitFor(() =>
      expect(mockAuthFetch).toHaveBeenCalledWith(
        "/api/workspace/myvault/groups/group-1/members/user-1",
        "remove group member",
        expect.objectContaining({ method: "DELETE" }),
      ),
    );
  });

  it("updates the selected group's application data capability", async () => {
    render(<GroupMembers />);
    const toggle = await screen.findByRole("switch", {
      name: "Allow application data",
    });
    await waitFor(() => expect(toggle).not.toBeDisabled());
    fireEvent.click(toggle);
    await waitFor(() =>
      expect(mockAuthFetch).toHaveBeenCalledWith(
        "/api/workspace/myvault/groups/group-1/application-data",
        "update group application data",
        expect.objectContaining({
          method: "PUT",
          body: JSON.stringify({ allowed: true }),
        }),
      ),
    );
    expect(toggle).toBeChecked();
  });

  it("creates a group from an empty workspace and selects it for membership", async () => {
    mockGroups = [];
    const created = {
      id: "group-new",
      name: "editors",
      display_name: "Editors",
    };
    mockAuthFetch.mockImplementation(async (_url, tag) => {
      if (tag === "create group") return created;
      if (tag === "list group members") return [];
      if (tag === "load group application data") return { allowed: false };
      throw new Error(`Unexpected request: ${tag}`);
    });
    mockRefresh.mockImplementation(async () => {
      mockGroups = [created];
    });
    render(<GroupMembers />);
    fireEvent.change(screen.getByRole("textbox", { name: "Group name" }), {
      target: { value: " editors " },
    });
    fireEvent.change(screen.getByRole("textbox", { name: "Display name" }), {
      target: { value: " Editors " },
    });
    fireEvent.click(screen.getByRole("button", { name: "Create group" }));
    await waitFor(() =>
      expect(mockAuthFetch).toHaveBeenCalledWith(
        "/api/workspace/myvault/groups",
        "create group",
        expect.objectContaining({
          method: "POST",
          headers: {
            "x-workspace-id": "workspace-1",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ name: "editors", display_name: "Editors" }),
        }),
      ),
    );
    expect(
      await screen.findByRole("combobox", { name: "Workspace user" }),
    ).toBeInTheDocument();
    expect(mockRefresh).toHaveBeenCalledTimes(1);
  });

  it("keeps group details and reports a failed create so the admin can retry", async () => {
    mockGroups = [];
    mockAuthFetch.mockRejectedValue(new Error("Group name already exists"));
    render(<GroupMembers />);
    const name = screen.getByRole("textbox", { name: "Group name" });
    const display = screen.getByRole("textbox", { name: "Display name" });
    expect(screen.getByRole("button", { name: "Create group" })).toBeDisabled();
    fireEvent.change(name, { target: { value: "reviewers" } });
    fireEvent.change(display, { target: { value: "Reviewers" } });
    fireEvent.click(screen.getByRole("button", { name: "Create group" }));
    expect(
      await screen.findByText("Group name already exists"),
    ).toBeInTheDocument();
    expect(name).toHaveValue("reviewers");
    expect(display).toHaveValue("Reviewers");
  });
});
